/**
 * ModelSelector — pill picker + Compare toggle + dynamic credit-cost preview.
 *
 * Props:
 *   type         "text" | "image"
 *   action       one of CREDIT_COSTS keys ("script" | "caption" | "titles" | "thumbnail" | "competitor")
 *   model        currently selected model key (or null = default)
 *   compare      bool
 *   onChange     (next) => void  with next = { model, compare }
 *   className    optional
 *
 * The component fetches /ai/models once on mount and caches it.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { GitDiff, Cpu } from "@phosphor-icons/react";

let _modelsCache = null;
let _modelsPromise = null;

async function fetchModels() {
  if (_modelsCache) return _modelsCache;
  if (!_modelsPromise) {
    _modelsPromise = api.get("/ai/models").then(r => {
      _modelsCache = r.data;
      return _modelsCache;
    });
  }
  return _modelsPromise;
}

export const ModelSelector = ({ type = "text", action, model, compare, onChange, className = "" }) => {
  const [config, setConfig] = useState(null);

  useEffect(() => { fetchModels().then(setConfig); }, []);

  const models = useMemo(() => {
    if (!config) return [];
    return type === "image" ? config.image_models : config.text_models;
  }, [config, type]);

  const baseCost = config?.credit_costs?.[action] ?? 1;

  const totalCost = useMemo(() => {
    if (!config) return baseCost;
    if (compare) {
      return models.reduce((sum, m) => sum + Math.max(1, Math.ceil(baseCost * m.multiplier)), 0);
    }
    const active = models.find(m => m.key === model) || models[0];
    return active ? Math.max(1, Math.ceil(baseCost * active.multiplier)) : baseCost;
  }, [config, compare, model, models, baseCost]);

  if (!config) {
    return <div className={`shimmer h-12 rounded bg-white/5 ${className}`} data-testid={`model-selector-loading-${action}`} />;
  }

  const selected = model || config.defaults[type];

  return (
    <div className={`space-y-3 ${className}`} data-testid={`model-selector-${action}`}>
      <div className="flex items-center justify-between">
        <label className="text-xs text-white/60 uppercase tracking-wider flex items-center gap-1.5">
          <Cpu size={12} /> Model
        </label>
        <span className="text-xs text-white/40 font-mono" data-testid={`model-cost-${action}`}>
          {totalCost} credit{totalCost === 1 ? "" : "s"}
        </span>
      </div>

      <div className={`flex flex-wrap gap-2 ${compare ? "opacity-40 pointer-events-none" : ""}`} aria-disabled={compare}>
        {models.map(m => (
          <button
            key={m.key}
            type="button"
            onClick={() => onChange({ model: m.key, compare: false })}
            className={`px-3 py-1.5 rounded-full text-xs transition-all border ${selected === m.key && !compare ? "bg-[#FF3B6B] text-white border-[#FF3B6B]" : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"}`}
            data-testid={`model-pill-${action}-${m.key}`}
            title={m.blurb}
          >
            {m.label}
            <span className="ml-1.5 text-[10px] text-white/40 font-mono">{m.multiplier}×</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={() => onChange({ model: model || config.defaults[type], compare: !compare })}
        className={`w-full text-left px-3 py-2 rounded-lg border transition-all flex items-center gap-2 ${compare ? "bg-[#00E5FF]/10 border-[#00E5FF]/40 text-[#00E5FF]" : "bg-white/5 border-white/10 hover:bg-white/10 text-white/70"}`}
        data-testid={`compare-toggle-${action}`}
      >
        <GitDiff size={14} weight={compare ? "fill" : "regular"} />
        <span className="text-xs flex-1">
          {compare ? `Comparing all ${models.length} models` : "Compare across all models"}
        </span>
        {compare && <span className="text-[10px] font-mono text-[#00E5FF]/70">A/B/C</span>}
      </button>
      {compare && (
        <p className="text-[10px] text-white/40 leading-relaxed" data-testid={`compare-hint-${action}`}>
          Compare runs {models.length} models in parallel — usually 30–45 seconds.
        </p>
      )}
    </div>
  );
};

export default ModelSelector;
